import React, { useMemo } from 'react'
import Trans from 'next-translate/Trans'
import { MarketingDrawer } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { apiRoutes } from '@app/templates/rest-api'
import { ApiCell } from '@app/components/general/cells/api-info-cell'
import { Header } from '@app/components/general/header'
import { SectionContainer } from '@app/components/stateless/containers/section-container'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { useAuthContext } from '@app/components/providers/auth'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { blueLink } from '@app/components/stateless/typo/link-style'
import { companyName } from '@app/configs'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/api-info',
    }),
  }
}

type ParamObject = {
  desc?: string
  [x: string]: any
}

type ParamRecord = Record<string, ParamObject>

type ParamDefs = {
  websiteParams: ParamRecord
  authParams: ParamRecord
  crawlParams: ParamRecord
  generalParams: ParamRecord
}

const defReMapper = (key: string) => {
  let mapper = key

  switch (key) {
    case 'pageUrl': {
      mapper = 'url'
      break
    }
    case 'search': {
      mapper = 'search-issues'
      break
    }
    // TODO: add conditional download endpoint
    case 'download': {
      mapper = 'download-excel'
      break
    }

    default: {
      break
    }
  }
  return mapper
}

// pluck the params to the record
const pluckParams = (key: string, params: ParamRecord, def: ParamRecord) => {
  // if the key exist in the params object map to the record
  if (key in params) {
    const defRemapper = defReMapper(key)
    if (typeof params[key] === 'boolean') {
      params[key] = def[defRemapper]
    } else if (def[defRemapper] && typeof def[defRemapper] === 'object') {
      params[key].desc = def[defRemapper].desc
    }
  }

  return params
}

const getRouteDef = (
  paramDefs: ParamDefs,
  paramType: keyof ParamDefs | Record<string, any>,
  params?: Record<string, any>
) => {
  // Default Extract record to object completely
  if (!params && typeof paramType === 'string') {
    if (paramType in paramDefs) {
      return paramDefs[paramType]
    }
  }
  // handle fallback params extraction
  if (params) {
    // THE param object to use for data
    const pDef =
      paramDefs[typeof paramType === 'string' ? paramType : 'websiteParams']
    const gDef = paramDefs['generalParams']

    // handle URLS
    pluckParams('url', params, pDef)
    pluckParams('csv', params, pDef)
    pluckParams('offset', params, gDef)
    pluckParams('download', params, gDef)
    pluckParams('search', params, gDef)
    pluckParams('domain', params, gDef)
    pluckParams('deleteMany', params, gDef)
    pluckParams('pageUrl', params, paramDefs['websiteParams'])

    return params
  }
}

// TODO: APi info parsing route
function ApiInfo() {
  const { account } = useAuthContext()
  const { t } = useTranslation('api-info')

  const { copyText, routeParams, requiredText } = useMemo(() => {
    const websiteParams: ParamRecord = t('websiteParamDefs', undefined, {
      returnObjects: true,
    })
    const authParams: ParamRecord = t('authParamsDefs', undefined, {
      returnObjects: true,
    })
    const crawlParams: ParamRecord = t('crawlParamsDefs', undefined, {
      returnObjects: true,
    })
    const generalParams: ParamRecord = t('generalParamsDefs', undefined, {
      returnObjects: true,
    })

    return {
      copyText: t('copy'),
      requiredText: t('required'),
      routeParams: {
        websiteParams,
        authParams,
        crawlParams,
        generalParams: generalParams,
      },
    }
  }, [t])

  return (
    <MarketingDrawer authenticated={account?.authed} loading={false}>
      <SectionContainer block>
        <Header>{t('header')}</Header>
        <div>
          <div className='space-y-2 pb-4'>
            <h2 className={'text-xl text-gray-900 dark:text-gray-50'}>
              <Trans
                i18nKey='api-info:subheader'
                components={[<strong key={'s-1'} className='font-bold' />]}
              />
            </h2>
            <Paragraph>
              <Trans
                i18nKey='api-info:subtitle'
                values={{ companyName }}
                components={[
                  <b key={'1'} />,
                  <a
                    href={
                      'https://github.com/a11ywatch/a11ywatch/tree/main/clients'
                    }
                    className={blueLink}
                    target={'_blank'}
                    rel={'noreferrer'}
                    key={'2'}
                  />,
                  <a href={'/playground'} className={blueLink} key={'3'} />,
                  <a href={'/grpc-docs'} className={blueLink} key={'4'} />,
                ]}
              />
            </Paragraph>
          </div>

          <ul className='space-y-3 py-2 list-none'>
            {apiRoutes.map((ro, i: number) => {
              return (
                <li key={ro.title} id={ro.id}>
                  <div className='py-4'>
                    <div className='py-2 pr-4 pl-1 md:pl-2 text-[#0E1116] border-l-4 border-[#0E1116] rounded dark:text-white dark:border-white'>
                      <h3 className='text-2xl md:text-2xl font-bold'>
                        <a href={`#${ro.id}`}>{t(`sections.${i}.title`)}</a>
                      </h3>
                    </div>
                  </div>
                  <ul className='space-y-3 py-2 list-none'>
                    {ro.routes.map((route: any, ii: number) => {
                      const routeTitle = t(`sections.${i}.routes.${ii}.title`)
                      const routeDescriptions = t(
                        `sections.${i}.routes.${ii}.description`
                      )
                      const routeKey = `api-info-route-${i}-${ii}`

                      return (
                        <ApiCell
                          key={routeKey}
                          token={account?.token}
                          route={route}
                          routeTitle={routeTitle}
                          routeDescriptions={routeDescriptions}
                          defaultOpen={i === 0 && ii === 0}
                          keyVisible={account?.authed}
                          id={routeKey}
                          copyText={copyText}
                          requiredText={requiredText}
                          routeParams={getRouteDef(
                            routeParams,
                            route.paramsType,
                            route.params
                          )}
                        />
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>

          <div className='border rounded inline-block px-4 py-3 dark:border-gray-500'>
            <Paragraph>
              <Trans
                values={{
                  companyName,
                }}
                i18nKey='api-info:endclause'
                components={[<b key={'b-1'} />]}
              />
            </Paragraph>
          </div>
        </div>
      </SectionContainer>
    </MarketingDrawer>
  )
}

export default metaSetter(
  { ApiInfo },
  {
    translation: 'api-info',
  }
)
